<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">{{ title }}</h4>
      </div>
      <div class="d-flex align-items-center">
        <export-sidebar
          sidebar-id="sidebar-locations-export"
          storage-key="sidebar_locations_export"
          :columns="exportableFields"
          :data-to-export="dataToExport"
          :data-mapper="mapLocationForExport"
          file-name="Locations.xlsx"
          sheet-name="Locations"
          :is-busy="isBusy"
          :items-count="totalRows"
        />
        <b-button id="table-settings" class="custom-btn-secondary mr-1" v-b-toggle.sidebar-fields>
          <v-icon name="buttons/cog" :size="13"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip"  noninteractive>
          Колонки
        </b-tooltip>
        <b-btn v-if="canEditLocations()" class="custom-btn-primary" :disabled="isBusy" @click="onCreate()">
          <b-spinner v-if="isBusy" small/>
          <v-icon name="buttons/plus" :size="12"/>
          Новый объект
        </b-btn>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :query.sync="filters.query"
          :is-busy="isBusy"
          :filter-tags="activeFilterTags"
          @remove-tag="removeFilterTag"
          @submit="onSearch"
          @clear="onClearFilters"
        >
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Заказчик</label>
            <customer-select
              id="input-customer-select"
              ref="customer_select"
              v-model="filters.customer_id"
              @onSelect="onSelectCustomer"
            />
          </b-col>
          <b-col md="6" cols="12">
            <label class="small text-muted">Города</label>
            <b-form-select
              id="input-town"
              size="sm"
              v-model="filters.town"
            >
              <option :value="-1">Все города</option>
              <option v-for="option in towns" :value="option.id">
                {{ option.name }}
              </option>
            </b-form-select>
          </b-col>
        </filters-drawer>
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container">
          <b-table
            id="locations"
            ref="locations"
            class="custom-table mb-0 text-small"
            sticky-header
            :tbody-tr-class="tbodyRowClass"
            select-mode="multi"
            responsive
            :busy.sync="isBusy"
            :items="items"
            :fields="renderedFields"
            no-provider-sorting
            no-local-sorting
            @sort-changed="sortChanged"
            @row-clicked="onRowClicked"
            @row-dblclicked="onRowDblClicked"
          >
            <template #table-busy>
              <tables-skeleton :fields="renderedFields" :rows="4"/>
            </template>

            <template #head(selected)="row">
              <b-form-checkbox
                v-model="allSelected"
                :indeterminate="indeterminate"
                size="md"
                @change="selectPageRows"
              />
            </template>

            <template #cell(selected)="row">
              <b-form-checkbox
                size="md"
                :checked="row.item.selected"
                @click.native.stop
                @change="onRowClicked(row.item)"
              />
            </template>

            <template #cell(action)="row">
              <b-dropdown variant="link" toggle-class="text-decoration-none box-shadow-none" class="custom-dropdown" no-caret>
                <template #button-content>
                  <v-icon name="buttons/ellipsis_vertical" :size="15"/>
                </template>
                <b-dropdown-item variant="success" @click.stop="onView(row.item)">
                  <v-icon name="buttons/eye" :size="14" class="mr-2"/>
                  Смотреть
                </b-dropdown-item>
                <b-dropdown-item variant="primary" v-if="canEditLocations()" @click="onEdit(row.item)">
                  <v-icon name="buttons/edit" :size="14" class="mr-2"/>
                  Редактировать
                </b-dropdown-item>
                <b-dropdown-item v-if="canEditLocations()" variant="danger" @click="onRemove(row)">
                  <v-icon name="buttons/trash" :size="14" class="mr-2"/>
                  Удалить
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
        </div>

        <div class="page-pagination">
          <pagination-footer
            use-router
            :current-page.sync="currentPage"
            :total-rows="totalRows"
            :per-page.sync="perPage"
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          >
            <template #footer-update>
              <span v-if="selected.length > 0" class="text-small mr-2">
                Выбрано: <b>{{selected.length}}</b>
              </span>
              <span v-if="selected.length > 0" class="text-small vertical-divider"/>
            </template>
          </pagination-footer>
        </div>
      </template>

      <div v-else class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>{{ isFiltered ? 'Нет данных соответствующих фильтрам.' : 'Нет созданных объектов.' }}</h5>
        <div v-if="!isFiltered">
          <p>Создайте новый объект прямо сейчас</p>
          <b-btn v-if="canEditLocations()" class="custom-btn-primary mt-1" :disabled="isBusy" @click="onCreate()">
            <v-icon name="buttons/plus" :size="12" class="mr-1"/>
            Новый объект
          </b-btn>
        </div>
      </div>
    </div>

    <dynamic-tables
      storage-key="locations_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="id"
    />
  </div>
</template>

<script>
import locations from "../../network/locations";
import catalogs from "../../network/catalogs";
  import helpers from "../../utilites/helpers";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import ExportSidebar from "../../utilites/commonUi/ExportSidebar.vue";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";
    import CustomerSelect from "../Customers/CustomerSelect.vue";

export default {
  name: "Locations",
  components: { TablesSkeleton, PaginationFooter, ExportSidebar, CustomerSelect, FiltersDrawer, DynamicTables },
  mixins: [
    locations, catalogs,
    helpers, permissions, pageSearchService
  ],
  data() {
    return {
      title: 'Объекты',
      items: [],
      serviceStartField: {
        key: 'selected',
        label: '',
        class: 'text-center',
        thStyle: { width: '50px', 'min-width': '50px' },
      },
      fields: [
        {
          key: 'id',
          label: 'ID',
          thStyle: { width: '40px', 'min-width': '40px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'name',
          label: this.$t('message.locations.table.name'),
          thStyle: { width: '200px', 'min-width': '200px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'address',
          label: this.$t('message.locations.table.address'),
          thStyle: { width: '150px', 'min-width': '150px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'contract',
          label: this.$t('message.locations.table.contract'),
          thStyle: { width: '155px', 'min-width': '155px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'contact',
          label: this.$t('message.locations.table.contact'),
          thStyle: { width: '80px', 'min-width': '80px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'customer_name',
          label: this.$t('message.locations.table.customer_name'),
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
      ],
      serviceEndField: {
        key: 'action',
        label: this.$t('message.tasks.table.actions'),
        thStyle: { width: '80px', 'min-width': '80px' },
        class: 'text-center',
      },
      towns: [],
      sortBy: 'id',
      sortDesc: true,
      isBusy: false,
      filters: this.getDefaultFilters(),
      deleteRow: {
        id: null,
        name: '',
      },
      selected: [],
      allSelected: false,
      indeterminate: false,
    }
  },
  async created() {
    this.isBusy = true;
    const query = this.$route.query;

    if (query) {
      if (query.query) {
        this.filters.query = query.query;
      }

      if (query.town) {
        this.filters.town = query.town;
      }

      if (query.customer_id) {
        this.filters.customer_id = query.customer_id;
      }
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    this.towns = (await this.getTowns({})).data.items;

    await this.onSearch();
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      const allFields = [
        { ...this.serviceStartField, sticky: 'left' },
        ...this.visibleFields,
        { ...this.serviceEndField, sticky: 'right' }
      ];

      const getWidth = (f) => parseInt(f.thStyle && f.thStyle.width) || 0;

      const lastLeftItem = [...allFields].reverse().find(f => f.sticky === 'left');
      const lastLeftKey = lastLeftItem.key;
      const firstRightItem = allFields.find(f => f.sticky === 'right');
      const firstRightKey = firstRightItem.key;

      return allFields.map((field, index) => {
        const renderedResult = { ...field, thStyle: { ...field.thStyle } };
        const classes = field.class ? field.class.split(' ') : [];
        let offsetStyle = {};

        if (field.sticky === 'left') {
          const offset = allFields.slice(0, index)
            .filter(f => f.sticky === 'left')
            .reduce((sum, f) => sum + getWidth(f), 0);

          classes.push('custom-sticky-column');
          offsetStyle.left = `${offset}px`;
          renderedResult.thStyle.left = `${offset}px`;

          if (field.key === lastLeftKey) classes.push('custom-sticky-left-edge');
        }

        if (field.sticky === 'right') {
          const offset = allFields.slice(index + 1)
            .filter(f => f.sticky === 'right')
            .reduce((sum, f) => sum + getWidth(f), 0);

          classes.push('custom-sticky-column');
          offsetStyle.right = `${offset}px`;
          renderedResult.thStyle.right = `${offset}px`;

          if (field.key === firstRightKey) classes.push('custom-sticky-right-edge');
        }

        renderedResult.class = classes.join(' ');

        const styleObj = {
          ...offsetStyle,
          width: renderedResult.thStyle.width,
          'min-width': renderedResult.thStyle['min-width'],
          'max-width': renderedResult.thStyle['max-width'] || 'none'
        };

        const styleString = Object.keys(styleObj)
          .map(key => `${key}: ${styleObj[key]}`)
          .join('; ');

        renderedResult.tdAttr = {
          style: styleString
        };

        return renderedResult;
      });
    },
    dataToExport() {
      return this.selected.length > 0 ? this.selected : this.items;
    },
    exportableFields() {
      return this.fields
        .filter(field => field.exportable)
        .map(field => ({
          key: field.key,
          label: field.label,
          visible: field.visible,
        }));
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();

      if (this.filters.town !== defaults.town) {
        const foundItem = this.towns.find(s => s.id === this.filters.town);
        tags.push({ key: 'town', label: foundItem ? foundItem.name : 'Город' });
      }

      if (this.filters.customer_id !== defaults.customer_id) {
        tags.push({ key: 'customer', label: `Заказчик`,});
      }

      return tags;
    },
    isFiltered() {
      const hasQuery = !!this.filters.query && this.filters.query.trim().length > 0;
      const hasTags = this.activeFilterTags.length > 0;

      return hasQuery || hasTags;
    },
  },
  watch: {
    selected(newValue) {
      if (newValue.length === 0) {
        this.indeterminate = false;
        this.allSelected = false;
      } else if (newValue.length === this.items.length) {
        this.indeterminate = false;
        this.allSelected = true;
      } else {
        this.indeterminate = true;
        this.allSelected = false;
      }
    },
  },
  methods:{
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        town: this.filters.town,
        customer_id: this.filters.customer_id,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      this.isBusy = true;
      const result = await this.getLocations(data);

      if (!result || !result.data) {
        this.items = [];
        this.isBusy = false;
        this.totalRows = 0;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;
      this.updateSelected();
      this.isBusy = false;
    },
    onCreate() {
      this.$router.push({ name: 'LocationAdd'}).catch(()=>{});
    },
    onEdit(item) {
      this.$router.push({ name: 'LocationEdit', params: { location_id: item.id }}).catch(()=>{});
    },
    onView(item) {
      this.$router.push({ name: 'LocationView', params: { location_id: item.id}}).catch(()=>{});
    },
    onRowDblClicked(item) {
      if (this.hasPermission([PERM.LOCATION_EDIT])) {
        this.onEdit(item);
        return;
      }

      if (this.hasPermission([PERM.LOCATION_READ])) {
        this.onView(item);
        return;
      }
    },
    onRemove(row) {
      if (row.item) {
        this.deleteRow = row.item;
      }

      const title = this.$t('message.locations.modal.removeTitle');
      const message = `${this.$t("message.locations.modal.removeDesc")} ${this.deleteRow.name}?`;
      this.showModalDelete(title, message, async () => {
        const result = await this.deleteLocation(this.deleteRow);

        if (result.error) {
          this.showToast('Ошибка при удалении.', 'Ошибка', 'danger');
        } else {
          this.showToast('Объект удалён.', 'Успех', 'success');
          await this.getItems();
        }
      });
    },
    // Filters
    getDefaultFilters() {
      return {
        query: '',
        town: -1,
        customer_id: 0,
      };
    },
    onClearFilters() {
      const filters = this.getDefaultFilters();

      if (JSON.stringify(this.filters) === JSON.stringify(filters)) {
        return;
      }

      this.filters = filters;
      this.$refs.customer_select.onClear();
      this.onSearch();
    },
    removeFilterTag(tag) {
      if (tag.key === 'town') {
        this.filters.town = -1;
      }
      else if (tag.key === 'customer') {
        this.$refs.customer_select.onClear();
        this.filters.customer_id = 0;
      }
      this.onSearch();
    },
    onSelectCustomer(id) {
      this.filters.customer_id = id;
    },
    // Permissions
    canEditLocations() {
      return this.hasPermission([PERM.LOCATION_EDIT]);
    },
    // Export
    mapLocationForExport(item) {
      return {
        id: item.id,
        name: item.name,
        address: item.address,
        contract: item.contract,
        contact: item.contact,
        customer_name: item.customer_name,
      };
    },
    async selectPageRows(checked) {
      this.selected = checked ? [...this.items] : [];
      this.updateSelected();
    },
    onRowClicked(item) {
      // Ищем уже выбранные
      const existItem = this.selected.findIndex(e => e.id === item.id);

      if (existItem !== -1) {
        this.selected.splice(existItem, 1);
      } else {
        this.selected.push(item);
      }
      this.updateSelected();
    },
    updateSelected() {
      if (!this.items) return;

      this.items.forEach((item) => {
        const isSelected = this.selected.some(e => e.id === item.id);
        this.$set(item, 'selected', isSelected);
      });
    },
    tbodyRowClass(item) {
      return item && item.selected ? ["b-table-row-selected", "table-primary", "pointer-cell"] : ["pointer-cell"];
    },
  },
}
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/components/Locations/Locations.vue